// Bridges: a chain of new squares joining two banks of existing ground (§26 C9).

import { fileOf, rankOf, square } from "../board";
import { hasCell, type Position } from "../position";
import { hasStructure, type IndustryState } from "../industry/state";
import { RULES } from "../rules";

/** Why a bridge cannot be laid, or null. */
export type BridgeRefusal =
  | "no-squares"
  | "bridge-too-long"
  | "not-a-chain"
  | "already-board"
  | "square-occupied"
  | "no-bank"
  | "banks-adjacent";

/** Orthogonally touching, asked of the coordinates — a packed offset wraps at the file band. */
const orthogonal = (a: number, b: number): boolean =>
  Math.abs(fileOf(a) - fileOf(b)) + Math.abs(rankOf(a) - rankOf(b)) === 1;

/** The four squares orthogonally touching this one, built from coordinates for the same reason. */
const beside = (sq: number): number[] => {
  const file = fileOf(sq);
  const rank = rankOf(sq);
  return [
    square(file + 1, rank),
    square(file - 1, rank),
    square(file, rank + 1),
    square(file, rank - 1)
  ];
};

/** Existing ground touching this square — the banks an end of the chain rests on. */
export const banksOf = (position: Position, sq: number): number[] =>
  beside(sq).filter((at) => hasCell(position, at));

/**
 * Every rule about a bridge, in one place so the reducer and the board agree.
 *
 * Reasoning under §26 C9 in `docs/PLAN-MECHANICS.md`.
 */
export function refuseBridge(
  position: Position,
  industry: IndustryState,
  squares: readonly number[]
): BridgeRefusal | null {
  if (squares.length === 0) return "no-squares";
  if (squares.length > RULES.bridgeTiles) return "bridge-too-long";

  for (let i = 0; i < squares.length; i++) {
    const at = squares[i];
    if (squares.indexOf(at) !== i) return "not-a-chain";
    if (hasCell(position, at)) return "already-board";
    // A building's square is deliberately not a cell, so `hasCell` alone would
    // pave one and leave a square that is both.
    if (hasStructure(industry, at)) return "square-occupied";
    if (i > 0 && !orthogonal(squares[i - 1], at)) return "not-a-chain";
  }

  const first = banksOf(position, squares[0]);
  const last = banksOf(position, squares[squares.length - 1]);
  if (first.length === 0 || last.length === 0) return "no-bank";

  // A bridge has two banks: some pair of ends must be ground that was not
  // already touching. One qualifying pair is enough — a chain that reaches a
  // far bank is a bridge even where its near end also brushes a close one.
  let twoBanks = false;
  for (const a of first) {
    for (const b of last) {
      if (a === b) continue;
      twoBanks = true;
      if (!orthogonal(a, b)) return null;
    }
  }
  // No distinct pair at all is one bank, not two touching ones — a stub.
  return twoBanks ? "banks-adjacent" : "no-bank";
}
